import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSubscriptionsQuery } from "@/partner/entities/subscriptions";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { interpolationFormat } from "@/partner/shared/lib";
import { ErrorMessage } from "@/shared/components";
import { formatAmountObject } from "@/partner/features/products";
import { buildSubscriptionsFilter } from "../model/buildSubscriptionsFilter";
import type { SubscriptionsListQueryType } from "@/types/graphql/partner.generated";
import {
  RENEW_ERROR_REASON_LABEL_KEYS,
  SUBSCRIPTION_STATUS_COLORS,
  SUBSCRIPTION_STATUS_LABEL_KEYS,
  formatPastDue,
} from "../model/subscriptionLabels";
import { SubscriptionDetailsDialog } from "./SubscriptionDetailsDialog";

type SubscriptionRow =
  SubscriptionsListQueryType["subscriptions"]["nodes"][number];

export const SubscriptionsList: FC = () => {
  const { t, i18n } = useTranslation();
  const {
    filter,
    onChangeParam,
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage, id: deepLinkId },
  } = useFilter();

  const { data, loading, error } = useSubscriptionsQuery({
    // page сбрасывается в 0 самим FilterProvider при любом изменении
    // filter[...] (см. changeParams), отдельного сброса здесь не нужно.
    variables: { page, rowsPerPage, filter: buildSubscriptionsFilter(filter) },
    fetchPolicy: "cache-and-network",
  });

  // useMemo, а не литерал `?? []`: без него ссылка на `rows` менялась бы на
  // каждый рендер и ниже переоткрывала бы deep-link useEffect бесконечно.
  const rows = useMemo(
    () => data?.subscriptions?.nodes ?? [],
    [data?.subscriptions?.nodes]
  );
  const count = data?.subscriptions?.count ?? 0;

  // Диалог деталей открывается локальным useState, как в PurchasesList, но
  // дополнительно может быть открыт deep-link'ом `?id=<subscriptionId>` из
  // PurchaseDetailsDialog ("Посмотреть подписку"). `params.id` уже доступен
  // через FilterProvider — его `cleanedParams` включает произвольные
  // top-level query-ключи, а не только page/rowsPerPage.
  const [selectedSubscription, setSelectedSubscription] =
    useState<SubscriptionRow | null>(null);

  useEffect(() => {
    if (!deepLinkId) return;
    const row = rows.find((r) => r.id === deepLinkId);
    if (row) setSelectedSubscription(row);
  }, [deepLinkId, rows]);

  const emptyValue = t("common.emptyDash");

  const formatStatus = (row: SubscriptionRow): string =>
    t(SUBSCRIPTION_STATUS_LABEL_KEYS[row.status]);

  // Подсказка чипа «Просрочена»: причина и ход попыток (спека viewer-money).
  const pastDueHint = (row: SubscriptionRow): string => {
    const progress = formatPastDue(row, t, (value) =>
      formatDate(value, "short")
    );
    if (!progress) return "";
    const reason = row.renewErrorReason
      ? t(RENEW_ERROR_REASON_LABEL_KEYS[row.renewErrorReason])
      : null;
    return reason ? `${reason}. ${progress}` : progress;
  };

  const formatRenew = (row: SubscriptionRow): string =>
    t(
      row.renewSubscription
        ? "subscriptions.list.renewOn"
        : "subscriptions.list.renewOff"
    );

  const formatDate = (value: string, format: "short" | "time"): string =>
    interpolationFormat(value, format, i18n.language);

  // Нетто-выручка подписки (ProductSubscription.revenue). Ноль (у
  // перенесённых подписок без платежей и у бесплатных циклов) показываем
  // прочерком, как и прочие пустые значения списка.
  const formatRevenue = (row: SubscriptionRow): string =>
    row.revenue.value === 0 ? emptyValue : formatAmountObject(row.revenue);

  if (error) return <ErrorMessage error={error} />;

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (count === 0) {
    return <EmptyListCard title={t("subscriptions.list.empty")} />;
  }

  return (
    <>
      <Card elevation={0} sx={{ overflowX: "auto" }}>
        <Table size="medium">
          <TableHead>
            <TableRow>
              <TableCell>{t("subscriptions.list.statusColumn")}</TableCell>
              <TableCell sx={{ minWidth: 240 }}>
                {t("subscriptions.list.subscriptionColumn")}
              </TableCell>
              {/* Под числом циклов выручка подписки, поэтому и в шапке
                  вторая строка. */}
              <TableCell>
                <Typography variant="inherit" noWrap>
                  {t("subscriptions.list.cyclesColumn")}
                </Typography>
                <Typography variant="inherit" color="textSecondary" noWrap>
                  {t("subscriptions.list.revenueColumn")}
                </Typography>
              </TableCell>
              <TableCell>{t("subscriptions.list.promocodeColumn")}</TableCell>
              <TableCell>{t("subscriptions.list.renewColumn")}</TableCell>
              {/* Даты фильтра бьют по дате создания подписки (резолвер
                  `subscriptions`), а «Следующее списание» уезжает вперёд на
                  каждом продлении: без `startAt` выборку «за сентябрь» было
                  бы не связать с тем, что видно в таблице. */}
              <TableCell align="right" sx={{ width: 160 }}>
                {t("subscriptions.list.startAtColumn")}
              </TableCell>
              <TableCell align="right" sx={{ width: 160 }}>
                {t("subscriptions.list.nextRenewalColumn")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell sx={{ maxWidth: 220 }}>
                  <Chip
                    size="small"
                    color={SUBSCRIPTION_STATUS_COLORS[row.status]}
                    label={formatStatus(row)}
                  />
                  {/* Причина и ход попыток текстом под чипом (ревью M6):
                      подсказка на тач-экране недоступна. */}
                  {pastDueHint(row) && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      component="div"
                      sx={{ mt: 0.5 }}
                    >
                      {pastDueHint(row)}
                    </Typography>
                  )}
                </TableCell>

                {/* Детали подписки открывает только эта ячейка: остальная
                    строка некликабельна, чтобы не спорить с подсказкой
                    промокода и выделением текста. */}
                <TableCell
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedSubscription(row)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelectedSubscription(row);
                    }
                  }}
                  sx={{
                    minWidth: 240,
                    maxWidth: 320,
                    cursor: "pointer",
                    "&:hover .subscription-title, &:focus-visible .subscription-title":
                      { textDecoration: "underline" },
                  }}
                >
                  <Typography
                    className="subscription-title"
                    variant="body2"
                    color="textPrimary"
                  >
                    {row.product?.title ?? emptyValue}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    title={row.buyerEmail ?? undefined}
                    noWrap
                  >
                    {row.buyerEmail ?? emptyValue}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="body2" color="textPrimary" noWrap>
                    {row.billingCyclesComplete}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" noWrap>
                    {formatRevenue(row)}
                  </Typography>
                </TableCell>

                {/* Промокод, с которым взята подписка; у перенесённых со
                    старой платформы это код оттуда. Название в подсказке. */}
                <TableCell>
                  {row.promocode ? (
                    <Tooltip title={row.promocode.title ?? ""}>
                      <Typography variant="body2" noWrap>
                        {row.promocode.code}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2">{emptyValue}</Typography>
                  )}
                </TableCell>

                {/* Read-only индикатор: партнёр не управляет автопродлением
                    (спека §241), только видит его состояние. Чип как
                    RenewStatus.js в референсе. */}
                <TableCell>
                  <Chip
                    size="small"
                    color={row.renewSubscription ? "success" : "warning"}
                    label={formatRenew(row)}
                  />
                </TableCell>

                <TableCell align="right" sx={{ width: 160 }}>
                  <Typography variant="body2" color="textPrimary" noWrap>
                    {formatDate(row.startAt, "short")}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" noWrap>
                    {formatDate(row.startAt, "time")}
                  </Typography>
                </TableCell>

                <TableCell align="right" sx={{ width: 160 }}>
                  <Typography variant="body2" color="textPrimary" noWrap>
                    {formatDate(row.currentPeriodEnd, "short")}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" noWrap>
                    {formatDate(row.currentPeriodEnd, "time")}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={count}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 100]}
          labelRowsPerPage={t("common.pagination.rowsPerPage")}
          labelDisplayedRows={({ from, to, count }) =>
            t("common.pagination.displayedRows", {
              from,
              to,
              count:
                count !== -1 ? count : t("common.pagination.moreThan", { to }),
            })
          }
        />
      </Card>

      <SubscriptionDetailsDialog
        open={Boolean(selectedSubscription)}
        subscription={selectedSubscription}
        onClose={() => {
          setSelectedSubscription(null);
          // Чистим deep-link параметр, чтобы повторное открытие/закрытие
          // диалога другой строкой не переоткрывало эту же подписку.
          if (deepLinkId) onChangeParam("id", "");
        }}
      />
    </>
  );
};
